import { useServerState } from "@api-client/useServerState";
import { handleApiError } from "@api-client/errorHandler";
import { createHeaders } from "@project-mgmt/api-client";
// Task API hooks
export function useTasks(workspaceId, projectId, filters) {
    const queryParams = new URLSearchParams();
    // Add workspace filter (required)
    if (workspaceId)
        queryParams.set("workspaceId", workspaceId);
    // Add optional filters
    if (projectId)
        queryParams.set("projectId", projectId);
    if (filters?.status)
        queryParams.set("status", filters.status);
    if (filters?.priority)
        queryParams.set("priority", filters.priority);
    if (filters?.assignedTo)
        queryParams.set("assignedTo", filters.assignedTo);
    if (filters?.sortBy)
        queryParams.set("sortBy", filters.sortBy);
    if (filters?.sortOrder)
        queryParams.set("sortOrder", filters.sortOrder);
    const queryString = queryParams.toString();
    const url = `/api/tasks${queryString ? `?${queryString}` : ""}`;
    return useServerState({
        key: ["tasks", workspaceId, projectId, filters],
        fetcher: async (signal) => {
            try {
                const response = await fetch(url, {
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to fetch tasks");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                // Handle the error through the global error handler
                throw handleApiError(error);
            }
        },
        onError: (error) => {
            console.error("Failed to fetch tasks:", error);
        },
    });
}
export function useTask(taskId, workspaceId) {
    return useServerState({
        key: ["task", taskId, workspaceId],
        fetcher: async (signal) => {
            try {
                const response = await fetch(`/api/tasks/${taskId}?workspaceId=${workspaceId}`, {
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to fetch task");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        onError: (error) => {
            console.error("Failed to fetch task:", error);
        },
    });
}
export function useCreateTask(workspaceId) {
    return useServerState({
        key: ["tasks:create", workspaceId],
        fetcher: async (input, signal) => {
            try {
                const response = await fetch(`/api/tasks?workspaceId=${workspaceId}`, {
                    method: "POST",
                    headers: createHeaders(workspaceId),
                    body: JSON.stringify(input),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to create task");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to create task:", error);
        },
    });
}
export function useUpdateTask(taskId, workspaceId) {
    return useServerState({
        key: ["tasks:update", taskId, workspaceId],
        fetcher: async (input, signal) => {
            try {
                const response = await fetch(`/api/tasks/${taskId}?workspaceId=${workspaceId}`, {
                    method: "PUT",
                    headers: createHeaders(workspaceId),
                    body: JSON.stringify(input),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to update task");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to update task:", error);
        },
    });
}
export function useDeleteTask(workspaceId) {
    return useServerState({
        key: ["tasks:delete", workspaceId],
        fetcher: async (taskId, signal) => {
            try {
                const response = await fetch(`/api/tasks/${taskId}?workspaceId=${workspaceId}`, {
                    method: "DELETE",
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to delete task");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to delete task:", error);
        },
    });
}
// Task comments API hooks
export function useTaskComments(taskId, workspaceId) {
    return useServerState({
        key: ["tasks", taskId, "comments", workspaceId],
        fetcher: async (signal) => {
            try {
                const response = await fetch(`/api/tasks/${taskId}/comments?workspaceId=${workspaceId}`, {
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to fetch task comments");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        onError: (error) => {
            console.error("Failed to fetch task comments:", error);
        },
    });
}
export function useCreateTaskComment(taskId, workspaceId) {
    return useServerState({
        key: ["tasks:comments:create", taskId, workspaceId],
        fetcher: async (input, signal) => {
            try {
                const response = await fetch(`/api/tasks/${taskId}/comments?workspaceId=${workspaceId}`, {
                    method: "POST",
                    headers: createHeaders(workspaceId),
                    body: JSON.stringify(input),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to create comment");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to create comment:", error);
        },
    });
}
export function useUpdateTaskComment(taskId, commentId, workspaceId) {
    return useServerState({
        key: ["tasks:comments:update", taskId, commentId, workspaceId],
        fetcher: async (input, signal) => {
            try {
                const response = await fetch(`/api/tasks/${taskId}/comments/${commentId}?workspaceId=${workspaceId}`, {
                    method: "PUT",
                    headers: createHeaders(workspaceId),
                    body: JSON.stringify(input),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to update comment");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to update comment:", error);
        },
    });
}
export function useDeleteTaskComment(taskId, workspaceId) {
    return useServerState({
        key: ["tasks:comments:delete", taskId, workspaceId],
        fetcher: async (commentId, signal) => {
            try {
                const response = await fetch(`/api/tasks/${taskId}/comments/${commentId}?workspaceId=${workspaceId}`, {
                    method: "DELETE",
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to delete comment");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to delete comment:", error);
        },
    });
}
